import type { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { DrawerShell } from 'src/components/atlas/molecules/Drawer/DrawerShell';
import { EmptyState } from 'src/components/atlas/molecules/EmptyState';
import { useFieldManagementAuditEntriesQuery } from 'src/graphql/generated';
import { textStyleObject, typographySemantic } from 'src/theme/atlas';
import { DateFormats, formatDate } from 'src/utils/time';
import * as styles from './FieldManagementAuditLogDrawer.styles';

interface Props {
  open: boolean;
  onClose: () => void;
}

const COLUMNS = ['User', 'Event', 'Timestamp'];
const PAGE_SIZE = 10;

const COLUMN_SX = [styles.fixedCell, styles.descriptionCell, styles.fixedCell];

export const FieldManagementAuditLogDrawer: React.FC<Props> = ({
  open,
  onClose,
}) => {
  const { tokens } = useTheme<LensTheme>();
  const sp = tokens.semantic.spacing.desktop.gap;

  const [page, setPage] = useState(0);

  useEffect(() => {
    if (open) {
      setPage(0);
    }
  }, [open]);

  const { data, isLoading, isFetching, isError } =
    useFieldManagementAuditEntriesQuery(
      { input: { limit: PAGE_SIZE, offset: page * PAGE_SIZE } },
      { enabled: open, keepPreviousData: true },
    );

  const entries = data?.fieldManagementAuditEntries?.items ?? [];
  const count = data?.fieldManagementAuditEntries?.count ?? 0;

  let body: React.ReactNode;
  if (isLoading || isFetching) {
    body = (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', p: sp[3].value }}
        data-testid="field-management-audit-log-loading"
      >
        <CircularProgress />
      </Box>
    );
  } else if (isError) {
    body = (
      <Typography
        sx={{
          p: sp[2].value,
          ...textStyleObject(typographySemantic.bodySmall),
        }}
        data-testid="field-management-audit-log-error"
      >
        We couldn&apos;t load the audit log. Please try again.
      </Typography>
    );
  } else if (entries.length === 0) {
    body = (
      <Box data-testid="field-management-audit-log-empty">
        <EmptyState
          title="No events logged"
          description="Changes to fields and sections will appear here."
        />
      </Box>
    );
  } else {
    body = (
      <Box data-testid="field-management-audit-log">
        <TableContainer sx={styles.tableContainer}>
          <Table aria-label="Field management audit log" size="small">
            <TableHead>
              <TableRow>
                {COLUMNS.map((column, index) => (
                  <TableCell key={column} sx={COLUMN_SX[index]}>
                    {column}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((entry) => (
                <TableRow
                  key={entry.id}
                  data-testid="field-management-audit-log-row"
                >
                  <TableCell sx={styles.fixedCell}>
                    {entry.user ?? 'System'}
                  </TableCell>
                  <TableCell sx={styles.descriptionCell}>
                    {entry.description}
                  </TableCell>
                  <TableCell sx={styles.fixedCell}>
                    {formatDate(
                      entry.createdAt,
                      DateFormats.YYYY_MM_DD_TIME_SHORT,
                      false,
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={count}
          page={page}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
          onPageChange={(_event, nextPage) => setPage(nextPage)}
          data-testid="field-management-audit-log-pagination"
        />
      </Box>
    );
  }

  return (
    <DrawerShell
      open={open}
      title="Audit log"
      onRequestClose={onClose}
      hideActions
      testId="field-management-audit-log-drawer"
    >
      {body}
    </DrawerShell>
  );
};
